import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CheckBoxCustomFieldValue } from "@btnet/services-btnet/handlers/models/checkBoxCustomFieldValue";
import type { CurrencyCustomFieldValue } from "@btnet/services-btnet/handlers/models/currencyCustomFieldValue";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { DateCustomFieldValue } from "@btnet/services-btnet/handlers/models/dateCustomFieldValue";
import type { DropdownCheckListCustomFieldValue } from "@btnet/services-btnet/handlers/models/dropdownCheckListCustomFieldValue";
import type { DropdownCustomFieldValue } from "@btnet/services-btnet/handlers/models/dropdownCustomFieldValue";
import type { FileCustomFieldValue } from "@btnet/services-btnet/handlers/models/fileCustomFieldValue";
import type { LinkCustomFieldValue } from "@btnet/services-btnet/handlers/models/linkCustomFieldValue";
import type { ListOfSubsCustomFieldValue } from "@btnet/services-btnet/handlers/models/listOfSubsCustomFieldValue";
import type { ListOfUsersCustomFieldValue } from "@btnet/services-btnet/handlers/models/listOfUsersCustomFieldValue";
import type { MultiLineTextCustomFieldValue } from "@btnet/services-btnet/handlers/models/multiLineTextCustomFieldValue";
import type { MultiSelectSubsCustomFieldValue } from "@btnet/services-btnet/handlers/models/multiSelectSubsCustomFieldValue";
import type { MultiSelectUsersCustomFieldValue } from "@btnet/services-btnet/handlers/models/multiSelectUsersCustomFieldValue";
import type { TextCustomFieldValue } from "@btnet/services-btnet/handlers/models/textCustomFieldValue";
import type { WholeNumberCustomFieldValue } from "@btnet/services-btnet/handlers/models/wholeNumberCustomFieldValue";

import { CustomFieldDataType } from "types/enum";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import type { CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";
import { CheckBoxCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/CheckBoxCustomFieldHandler";
import { CurrencyCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/CurrencyCustomFieldHandler";
import { DateCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/DateCustomFieldHandler";
import { DropdownCheckListCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/DropdownCheckListCustomFieldHandler";
import { DropdownCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/DropdownCustomFieldHandler";
import { FileCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/FileCustomFieldHandler";
import { LinkCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/LinkCustomFieldHandler";
import { ListOfSubsCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/ListOfSubsCustomFieldHandler";
import { ListOfUsersCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/ListOfUsersCustomFieldHandler";
import { MultiLineTextCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/MultiLineTextCustomFieldHandler";
import { MultiSelectSubsCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/MultiSelectSubsCustomFieldHandler";
import { MultiSelectUsersCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/MultiSelectUsersCustomFieldHandler";
import { TextCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/TextCustomFieldHandler";
import { WholeNumberCustomFieldHandler } from "commonComponents/entity/customField/utils/customFieldTypes/WholeNumberCustomFieldHandler";

export type CustomFieldValue =
    | TextCustomFieldValue
    | DropdownCustomFieldValue
    | MultiLineTextCustomFieldValue
    | CheckBoxCustomFieldValue
    | WholeNumberCustomFieldValue
    | CurrencyCustomFieldValue
    | DateCustomFieldValue
    | FileCustomFieldValue
    | DropdownCheckListCustomFieldValue
    | ListOfUsersCustomFieldValue
    | ListOfSubsCustomFieldValue
    | MultiSelectUsersCustomFieldValue
    | MultiSelectSubsCustomFieldValue
    | LinkCustomFieldValue;

export type CustomFieldDataTypeName = CustomFieldValue["dataType"] | "None";

export function customFieldDataTypeName(dataType: CustomFieldDataType): CustomFieldDataTypeName {
    return CustomFieldDataType[dataType] as CustomFieldDataTypeName;
}

export function customFieldDataTypeValue(name: CustomFieldDataTypeName): CustomFieldDataType {
    return CustomFieldDataType[name];
}

/**
 * Context passed to renderAgGridCell for type-specific AG Grid cell rendering.
 */
export interface IAgGridCellContext<
    TValue = unknown,
    TDef extends CustomFieldDefinition = CustomFieldDefinition,
    TField extends CustomFieldValue = CustomFieldValue,
> {
    displayContent: ReactNode;
    value: TValue | null;
    canEdit: boolean;
    definition: TDef;
    field?: TField;
    onCellValueChange: (newValue: unknown) => void;
}

/**
 * Default AG Grid cell rendering — wraps display content in a standard div.
 */
export function defaultRenderAgGridCell(context: IAgGridCellContext): ReactNode {
    return <div className="custom-field-cell-content">{context.displayContent}</div>;
}

/**
 * Generic handler interface for each custom field type.
 * Each handler knows how to get/set/render values for its specific type.
 */
export interface ICustomFieldTypeHandler<
    TValue = unknown,
    TField extends CustomFieldValue = CustomFieldValue,
    TDef extends CustomFieldDefinition = CustomFieldDefinition,
> {
    /**
     * Gets the display value from a custom field value object
     */
    getValue(field: TField): TValue | null;

    /**
     * Sets the value on a custom field and returns the object with the value set
     */
    setValue(field: TField, value: TValue | null): TField;

    /**
     * Creates a new custom field value object
     */
    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: TValue | null,
    ): TField;

    /**
     * Compares two values for sorting.
     * Returns negative if valueA < valueB, positive if valueA > valueB, 0 if equal
     *
     * `sortByOptionOrder` is only meaningful for option-based types (dropdowns) — when true,
     * they compare by each option's configured sort order instead of alphabetically by text.
     */
    compare(
        valueA: TValue | null,
        valueB: TValue | null,
        definition?: TDef,
        sortByOptionOrder?: boolean,
    ): number;

    /**
     * Renders the display component for this custom field type.
     * `field` is the full custom field value object, available when extra metadata
     * beyond the extracted value is needed (e.g., File's fileName/downloadLink).
     */
    renderDisplay(value: TValue | null, definition: TDef, field?: TField): ReactNode;

    /**
     * Renders the editor component for this custom field type.
     * `field` is the full custom field value object, available when extra metadata
     * beyond the extracted value is needed.
     */
    renderEditor(
        value: TValue | null,
        customFieldId: number,
        onChange: (newValue: TValue | null) => void,
        onBlur: () => void,
        autoFocus?: boolean,
        definition?: TDef,
        field?: TField,
        formMode?: "always-editing" | "click-to-edit",
    ): ReactNode;

    /**
     * Returns the AG-Grid cell editor for this custom field type.
     * Can return:
     * - A string for built-in AG-Grid editors (e.g., "agTextCellEditor")
     * - A React component for custom editors
     * - undefined if no editor should be used
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getAgGridCellEditor(): string | React.ComponentType<any> | undefined;

    /**
     * Renders the AG Grid cell display for this custom field type.
     * Allows each type to customize how it appears in the grid cell renderer.
     * Most types use the default wrapper; File, Link, and MultiLineText provide custom rendering.
     */
    renderAgGridCell(context: IAgGridCellContext<TValue, TDef, TField>): ReactNode;
}

const PlaceholderCustomFieldHandler: ICustomFieldTypeHandler<null> = {
    getValue(): null {
        return null;
    },
    setValue(field): CustomFieldValue {
        return field;
    },
    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
    ): CustomFieldValue {
        return {
            dataType: "None",
            customFieldId,
            associatedType,
            associatedTypeId,
            selectedOptionId: -1,
            customFieldValueId: 0,
        } as unknown as CustomFieldValue;
    },
    compare(): number {
        return 0;
    },

    renderDisplay(): ReactNode {
        return <BTText size="normal-md" text="—" />;
    },

    renderEditor(): ReactNode {
        return <BTText size="normal-md" text="Not implemented" />;
    },

    getAgGridCellEditor() {
        return undefined;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};

/**
 * Registry mapping CustomFieldDataType to its handler
 */
const customFieldHandlers: Record<CustomFieldDataTypeName, ICustomFieldTypeHandler> = {
    Text: TextCustomFieldHandler,
    MultiLineText: MultiLineTextCustomFieldHandler,
    DropDownList: DropdownCustomFieldHandler,
    CheckBox: CheckBoxCustomFieldHandler,
    WholeNumber: WholeNumberCustomFieldHandler,
    ListOfUsers: ListOfUsersCustomFieldHandler,
    ListOfSubs: ListOfSubsCustomFieldHandler,
    Date: DateCustomFieldHandler,
    Currency: CurrencyCustomFieldHandler,
    File: FileCustomFieldHandler,
    DropDownCheckList: DropdownCheckListCustomFieldHandler,
    Link: LinkCustomFieldHandler,
    MultiSelectUsers: MultiSelectUsersCustomFieldHandler,
    MultiSelectSubs: MultiSelectSubsCustomFieldHandler,
    None: PlaceholderCustomFieldHandler,
};

/**
 * Gets the value of a custom field
 * @param customFieldValue
 * @returns
 */
export function getCustomFieldValue(customFieldValue: CustomFieldValue): unknown {
    const handler = customFieldHandlers[customFieldValue.dataType];
    return handler?.getValue(customFieldValue) ?? null;
}

/**
 * Sets a custom field value
 * Automatically picks the setter based on the field's dataType.
 */
export function setCustomFieldValue(
    customFieldValue: CustomFieldValue,
    value: unknown,
): CustomFieldValue {
    const handler = customFieldHandlers[customFieldValue.dataType];

    if (!handler) {
        console.warn(`No handler found for custom field data type: ${customFieldValue.dataType}`);
        return customFieldValue;
    }

    return handler.setValue(customFieldValue, value);
}

/**
 * Creates a new custom field value object.
 * Automatically picks the creator based on the dataType.
 */
export function createCustomFieldValue(
    customFieldId: number,
    dataType: CustomFieldDataTypeName,
    associatedType: CustomFieldAssociatedType,
    associatedTypeId: number,
    value: unknown,
): CustomFieldValue {
    const handler = customFieldHandlers[dataType];

    if (!handler) {
        console.warn(`No handler found for custom field data type: ${dataType}`);
        return PlaceholderCustomFieldHandler.createValue(
            customFieldId,
            associatedType,
            associatedTypeId,
            null,
        );
    }

    return handler.createValue(customFieldId, associatedType, associatedTypeId, value);
}

/**
 * Compares two custom field values for sorting.
 * Automatically picks the comparison logic based on the field's dataType.
 * Returns negative if valueA < valueB, positive if valueA > valueB, 0 if equal
 *
 * `sortByOptionOrder` only affects dropdown types — see ICustomFieldTypeHandler.compare.
 */
export function compareCustomFieldValues(
    valueA: unknown,
    valueB: unknown,
    dataType: CustomFieldDataTypeName,
    definition?: CustomFieldDefinition,
    sortByOptionOrder?: boolean,
): number {
    const handler = customFieldHandlers[dataType];

    if (!handler) {
        console.warn(`No handler found for custom field data type: ${dataType}`);
        return 0;
    }

    return handler.compare(valueA, valueB, definition, sortByOptionOrder);
}

/**
 * Renders the display component for a custom field value.
 * Automatically picks the component based on the field's dataType.
 * When dataType is provided, it overrides the value's built-in dataType for handler selection.
 */
export function renderCustomFieldValue(
    customFieldValue: CustomFieldValue | undefined,
    definition: CustomFieldDefinition,
): ReactNode {
    if (!customFieldValue) {
        return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
    }

    const handler = customFieldHandlers[customFieldValue.dataType];

    if (!handler) {
        return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
    }

    const value = handler.getValue(customFieldValue);
    return handler.renderDisplay(value, definition, customFieldValue);
}

/**
 * Renders the editor component for a custom field value.
 * Automatically picks the component based on the field's dataType.
 *
 * When customFieldValue is provided, extracts the value and customFieldId from it.
 * When customFieldValue is undefined (e.g., AG Grid adapter), falls back to
 * customFieldIdOverride for the element ID.
 */
export function renderCustomFieldEditor(
    customFieldValue: CustomFieldValue | undefined,
    customFieldDataType: CustomFieldDataTypeName,
    onChange: (newValue: unknown) => void,
    onBlur: () => void,
    autoFocus?: boolean,
    definition?: CustomFieldDefinition,
    customFieldIdOverride?: number,
    formMode?: "always-editing" | "click-to-edit",
): ReactNode {
    const handler = customFieldHandlers[customFieldDataType];

    if (!handler) {
        return <BTText size="normal-md" text="Not implemented" />;
    }

    if (!customFieldValue) {
        return handler.renderEditor(
            null,
            customFieldIdOverride ?? 0,
            onChange,
            onBlur,
            autoFocus,
            definition,
            undefined,
            formMode,
        );
    }

    const value = handler.getValue(customFieldValue);
    return handler.renderEditor(
        value,
        customFieldValue.customFieldId,
        onChange,
        onBlur,
        autoFocus,
        definition,
        customFieldValue,
        formMode,
    );
}

/**
 * Gets the AG-Grid cell editor for a custom field data type.
 * Returns either a string (for built-in AG-Grid editors like "agTextCellEditor")
 * or a React component (for custom editors).
 */
export function getAgGridCellEditor(
    dataType: CustomFieldDataTypeName,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): string | React.ComponentType<any> | undefined {
    const handler = customFieldHandlers[dataType];

    if (!handler) {
        console.warn(`No handler found for custom field data type: ${dataType}`);
        return undefined;
    }

    return handler.getAgGridCellEditor();
}

/**
 * Renders the AG Grid cell display for a custom field.
 * Delegates to the handler's renderAgGridCell method for type-specific rendering.
 */
export function renderAgGridCellDisplay(
    customFieldValue: CustomFieldValue | undefined,
    definition: CustomFieldDefinition,
    canEdit: boolean,
    onCellValueChange: (newValue: unknown) => void,
): ReactNode {
    const handler = customFieldHandlers[customFieldValue?.dataType ?? definition.dataType];

    if (!handler) {
        return (
            <div className="custom-field-cell-content">
                <BTText size="normal-md" text={friendlyDefaultDisplayValue} />
            </div>
        );
    }

    const value = customFieldValue ? handler.getValue(customFieldValue) : null;
    const displayContent = customFieldValue ? (
        handler.renderDisplay(value, definition, customFieldValue)
    ) : (
        <BTText size="normal-md" text={friendlyDefaultDisplayValue} />
    );

    return handler.renderAgGridCell({
        displayContent,
        value,
        canEdit,
        definition,
        field: customFieldValue,
        onCellValueChange,
    });
}
